const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const keys = (value, names) => object(value) && Object.keys(value).length === names.length && names.every(name => Object.hasOwn(value, name));
const text = (value, max) => typeof value === 'string' && value.length <= max;
const dated = value => typeof value === 'string' && /^20\d\d-\d\d-\d\d$/.test(value) && Number.isFinite(Date.parse(value + 'T12:00:00Z')) && new Date(value + 'T12:00:00Z').toISOString().slice(0, 10) === value;
const timestamp = value => value === null || text(value, 50) && Number.isFinite(Date.parse(value));
const reading = (value, min, max) => value === null || typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const after = (date, days) => new Date(Date.parse(date + 'T12:00:00Z') + days * 86400000).toISOString().slice(0, 10);
export const WEATHER_SNAPSHOT_MAX_BYTES = 65536;

/** Shared network/download boundary, usable without the remote app or a build runtime. */
export function validWeatherSnapshot(value) {
  try {
    if (!keys(value, ['slug','name','timeZone','localDate','elevationM','hourly','forecast'])
      || !text(value.slug, 100) || !/^[a-z0-9][a-z0-9-]*$/.test(value.slug) || !text(value.name, 200) || !value.name.trim()
      || !text(value.timeZone, 80) || !value.timeZone || /^[+-]/.test(value.timeZone) || !dated(value.localDate)
      || !reading(value.elevationM, -500, 9000) || !Array.isArray(value.hourly) || value.hourly.length > 2
      || !keys(value.forecast, ['updatedAt','days']) || !timestamp(value.forecast.updatedAt)
      || !Array.isArray(value.forecast.days) || value.forecast.days.length > 7) return false;
    new Intl.DateTimeFormat('en-GB', { timeZone: value.timeZone }).format();
    const dates = new Set();
    for (const day of value.hourly) {
      if (!keys(day, ['date','fetchedAt','source','kind','hours']) || !dated(day.date) || day.date < value.localDate || day.date > after(value.localDate, 1)
        || dates.has(day.date) || !timestamp(day.fetchedAt) || !(day.source === null || text(day.source, 120))
        || !['forecast','modelled-past','unknown'].includes(day.kind) || !Array.isArray(day.hours) || day.hours.length > 24) return false;
      dates.add(day.date);
      const hours = new Set();
      for (const hour of day.hours) {
        if (!keys(hour, ['hour','tempC','windKmh','gustKmh','precipMm','snowfallCm','cloudPct','visibilityM','freezingLevelM'])
          || !Number.isInteger(hour.hour) || hour.hour < 0 || hour.hour > 23 || hours.has(hour.hour)
          || !reading(hour.tempC, -100, 70) || !reading(hour.windKmh, 0, 500) || !reading(hour.gustKmh, 0, 500)
          || !reading(hour.precipMm, 0, 1000) || !reading(hour.snowfallCm, 0, 1000) || !reading(hour.cloudPct, 0, 100)
          || !reading(hour.visibilityM, 0, 200000) || !reading(hour.freezingLevelM, -1000, 20000)) return false;
        hours.add(hour.hour);
      }
    }
    dates.clear();
    for (const day of value.forecast.days) {
      if (!keys(day, ['date','tempMinC','tempMaxC','windKmh','precipMm','snowfallCm']) || !dated(day.date)
        || day.date < value.localDate || day.date > after(value.localDate, 6) || dates.has(day.date)
        || !reading(day.tempMinC, -100, 70) || !reading(day.tempMaxC, -100, 70) || !reading(day.windKmh, 0, 500)
        || !reading(day.precipMm, 0, 2000) || !reading(day.snowfallCm, 0, 2000)
        || day.tempMinC !== null && day.tempMaxC !== null && day.tempMinC > day.tempMaxC) return false;
      dates.add(day.date);
    }
    return new TextEncoder().encode(JSON.stringify(value)).length <= WEATHER_SNAPSHOT_MAX_BYTES;
  } catch { return false; }
}
export function weatherSnapshotCoverage(value) {
  const hourly = value.hourly.map(day => ({ date: day.date, hours: day.hours.filter(hour => Object.entries(hour).some(([key, value]) => key !== 'hour' && value !== null)).length })).filter(day => day.hours > 0);
  const daily = value.forecast.days.filter(day => Object.entries(day).some(([key, value]) => key !== 'date' && value !== null)).map(day => day.date);
  const dates = [...new Set([...hourly.map(day => day.date), ...daily])].sort();
  return { hourly, daily, first: dates[0] ?? null, last: dates.at(-1) ?? null };
}
export function snapshotSourceAge(at, now = Date.now()) {
  if (!at || !Number.isFinite(Date.parse(at)) || Date.parse(at) > now + 300000) return 'Source age unknown';
  return now - Date.parse(at) > 21600000 ? 'Source is over six hours old' : 'Source retrieved within six hours';
}
